import { Component, inject, OnDestroy, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { SubSink } from 'subsink';
import { ProfileApiService } from '../../apiCalls/profile-api.service';
import { CustomSnackbarService } from '../../shared/custom-snackbar.service';
import { RoutesConst } from '../../shared/route-const';

interface AddUserForm {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
}

export interface AddUserFormValue {
  firstName: string;
  lastName: string;
  email: string;
}

@Component({
  selector: 'app-edit-profile',
  imports: [MatButtonModule, MatFormField, MatInputModule, ReactiveFormsModule],
  templateUrl: './add-user.component.html',
  styleUrl: './add-user.component.scss',
})
export class AddUserComponent implements OnDestroy {
  private readonly subs = new SubSink();

  // Injected dependencies
  private readonly profileApiSvc = inject(ProfileApiService);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly customSnackbarSvc = inject(CustomSnackbarService);

  /** Reactive signals for state management */
  apiErrorMessage = signal<string>('');
  saveState = signal<boolean>(false);

  /** Form definition */
  form: FormGroup<AddUserForm> = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
  });

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue: AddUserFormValue = this.form.getRawValue();

    this.saveState.set(true);
    this.clearErrorMessage();

    this.customSnackbarSvc.requestSnackbar();

    this.subs.sink = this.profileApiSvc.addUser(formValue).subscribe({
      next: () => this.handleSuccess(),
      error: (error) => this.handleError(error),
    });
  }

  /**
   * Handles successful
   */
  private handleSuccess(): void {
    this.clearErrorMessage();
    this.saveState.set(false);
    this.routeBack();
    this.customSnackbarSvc.successSnackbar('Thank you for adding a new user. We are processing your request and will send an activation link to the new user very shortly—within one business day at the very latest.');
  }

  /**
   * Handles errors
   */
  private handleError(error: any): void {
    const errorMessage = error?.message || 'Unknown error occurred';
    this.apiErrorMessage.set(`: ${errorMessage}`);
    console.error('Error:', errorMessage);
    this.customSnackbarSvc.errorSnackbar();
    this.saveState.set(false);
  }

  /**
   * Clears the API error message
   */
  private clearErrorMessage(): void {
    this.apiErrorMessage.set('');
  }

  routeBack() {
    this.router.navigate([RoutesConst.manageUsers]);
  }

  // Getters for easier access in the template
  get firstName() {
    return this.form.controls.firstName;
  }
  get lastName() {
    return this.form.controls.lastName;
  }
  get email() {
    return this.form.controls.email;
  }
}
